import { Select } from '@smartgift/frontend-toolkit';
import { useQueryClient } from '@tanstack/react-query';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { QUERY_KEYS } from '@hero/constants';
import { contactServices } from '@hero/services';
import { useCreateTag } from '@hero/services/contact-service/hooks';
import { cleanObjectEmptyFields, createQueryKey } from '@hero/utils';

import { MultiValue } from './MultiValue';

export const TagSelect = React.forwardRef(
  ({ placeholder, value, onChange, optional, styles = {}, options = [], ...props }, ref) => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const { mutateAsync: createTag } = useCreateTag();

    let placeholderText = t('createTags');

    if (optional) {
      placeholderText += ` (${t('optional')})`;
    }

    return (
      <Select
        ref={ref}
        {...props}
        isMulti
        async
        creatable
        createNewText={t('createNewTag')}
        components={{
          MultiValue
        }}
        placeholder={placeholder || placeholderText}
        showSelectedOptionInPlaceholder
        hasMenuSearch
        value={value}
        onChange={onChange}
        options={options}
        defaultOptions
        key={value}
        styles={{
          menuPortal: () => {
            return {
              zIndex: 999
            };
          },
          ...styles
        }}
        onCreateOption={async inputValue => {
          const response = await createTag({
            name: inputValue
          });

          onChange([
            ...value,
            {
              label: response.name,
              value: response.id
            }
          ]);

          await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.useGetTagListKey] });

          return {
            label: response.name,
            value: response.id
          };
        }}
        loadOptions={async inputValue => {
          const params = {
            filters: {
              searchTerm: inputValue
            },
            options: {
              includeAutomations: true
            },
            pagination: {
              limit: 50,
              page: 1
            }
          };
          const response = await queryClient.fetchQuery({
            staleTime: Infinity,
            queryKey: createQueryKey(QUERY_KEYS.useGetTagListKey, params),
            queryFn: () => {
              return contactServices.listTags(cleanObjectEmptyFields(params));
            }
          });

          return response.docs
            .map(tag => ({
              label: tag.name,
              value: tag.id,
              automations: tag.automations
            }))
            .filter(tag => tag.label && tag.value);
        }}
      />
    );
  }
);

TagSelect.displayName = 'TagSelect';
